import {
  BrowserRouter as Router,
  Route,
  Routes,
  useLocation,
  matchPath,
  useNavigate
} from "react-router-dom";
import { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { changeLanguage } from "./slices/languageSlice";
import { initializeAuth } from "./slices/authSlice";
import { initializeSocketListeners } from "./slices/notificationsSlice";
import { initializeCoordinates, setCoordinates, setLocationName } from "./slices/locationSlice";
import { getUserCoordinates, reverseGeocode } from "./utils/geolocation";
import { logoutUser } from "./slices/authSlice";
import AuthService from "./services/auth/storage";
import ProtectedRoute from "./components/ProtectedRoute/ProtectedRoute";
import { jwtDecode } from "jwt-decode";
import LogoutModal from "./components/LogoutModal/LogoutModal";
import Header from "./components/Header/Header";
import Footer from "./components/Footer/Footer";
import LegalFooter from "./components/Footer/LegalFooter";
import HomeWrapper from "./pages/Home/HomeWrapper/HomeWrapper";
import SignUp from "./pages/SignUp/SignUp";
import SearchParent from "./pages/Search/SearchParent";
import NotificationDetail from "./pages/NotificationDetail/NotificationDetail";
import FullScreenImageOverlay from "./components/FullScreenImageOverlay/FullScreenImageOverlay";
import Profile from "./pages/Profile/Profile";
import Favorites from "./pages/Favorites/Favorites";
import NotFoundPage from "./components/ProtectedRoute/NotFoundPage";
import SplashScreen from "./components/SplashScreen/SplashScreen";
import ErrorRenderer from "./components/ErrorUI/ErrorRenderer/ErrorRenderer";
import TemporaryLocationBanner from "./components/TemporaryLocationBanner/TemporaryLocationBanner";
import CreateAccountBanner from "./components/CreateAccountBanner/CreateAccountBanner";
import PasswordResetRequest from "./pages/PasswordReset/Request/PasswordResetRequest";
import PasswordResetForm from "./pages/PasswordReset/Form/PasswordResetForm";
import useUnreadCounts from "./hooks/messages/useUnreadCounts";
import  useTitle from "./hooks/useTitle";
import { emitJoinUser } from "./services/socketClient";
import Messages from "./pages/Messages/Messages";
import NewPostBanner from "./components/NewPostBanner/NewPostBanner";
import AISearchPage from "./pages/AISearch/AISearchPage";
import TermsFullPage from "./pages/Legal/TermsFullPage";
import TermsSummaryPage from "./pages/Legal/TermsSummaryPage";
import PrivacyPage from "./pages/Legal/PrivacyPage";
import DmcaPage from "./pages/Legal/DmcaPage";
import ExtendConfirm from "./pages/ExtendConfirm/ExtendConfirm";
import EmailUnsubscribe from "./pages/EmailUnsubscribe/EmailUnsubscribe";
import { AiConsentProvider } from "./components/Consent/AiConsentProvider";
import CookieConsentBanner from "./components/Consent/CookieConsentBanner";
import GatedAnalytics from "./components/Consent/GatedAnalytics";
import "normalize.css";
import "./App.css";

const pageTransition = {
  in: {
    opacity: 1,
    transition: { duration: 0.15, ease: "easeOut" },
  },
  out: {
    opacity: 0,
    transition: { duration: 0.1, ease: "easeIn" },
  },
};

const routes = [
  { path: "/", element: HomeWrapper },
  { path: "/login", element: SignUp },
  { path: "/sign-up", element: SignUp },
  { path: "/reset-password", element: PasswordResetRequest },
  { path: "/reset-password/:token", element: PasswordResetForm },
  { path: "/search/:category", element: SearchParent },
  { path: "/search/:category/:subcategory?", element: SearchParent },
  { path: "/ai-search", element: AISearchPage },
  { path: "/terms", element: TermsFullPage },
  { path: "/terms-summary", element: TermsSummaryPage },
  { path: "/privacy", element: PrivacyPage },
  { path: "/dmca", element: DmcaPage },
  { path: "/post/:id", element: NotificationDetail },
  { path: "/extend/confirm", element: ExtendConfirm },
  { path: "/email/unsubscribe", element: EmailUnsubscribe },
  { path: "/profile", element: Profile },
  { path: "/favorites", element: Favorites },
  {
    path: "/messages",
    element: () => (
      <ProtectedRoute>
        <Messages />
      </ProtectedRoute>
    ),
  },
  {
    path: "/messages/:chatId",
    element: () => (
      <ProtectedRoute>
        <Messages />
      </ProtectedRoute>
    ),
  },
  {
    path: "*",
    element: NotFoundPage,
  }
];

function App() {
  return (
    <Router>
      <AiConsentProvider>
        <AppContent />
      </AiConsentProvider>
    </Router>
  );
}

function AppContent() {
  useTitle();
  const { i18n } = useTranslation();
  const location = useLocation();
  const background = location.state?.background;
  const language = useSelector((state) => state.language.language);
  const tempLocationName = useSelector((state) => state.location.tempLocationName);
  const isAuthInitialized = useSelector((state) => state.auth.isInitialized);
  const user = useSelector((state) => state.auth.user);
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const sessionCoordinates = useSelector((state) => state.location.coordinates);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [showLogoutModal, setShowLogoutModal] = useState(false);

  const loadGoogleMapsAPI = useCallback(() => {
    if (document.querySelector(`script[src*="maps.googleapis.com"]`)) return;

    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_API}&libraries=places&loading=async`;
    script.async = true;
    script.onerror = () => console.error("Failed to load Google Maps API");
    document.body.appendChild(script);
  }, []);

  useEffect(() => {
    const syncLanguage = () => {
      const resolved = i18n.resolvedLanguage || i18n.language;
      if (resolved && resolved !== language) {
        dispatch(changeLanguage(resolved));
      }
    };
    syncLanguage();
    i18n.on("languageChanged", syncLanguage);
    return () => i18n.off("languageChanged", syncLanguage);
  }, [i18n, dispatch, language]);

  useEffect(() => {
    const direction = language === "ar" || language === "he" ? "rtl" : "ltr";
    document.documentElement.setAttribute("dir", direction);
  }, [language]);

  useEffect(() => {
    dispatch(initializeAuth());
  }, [dispatch]);

  useEffect(() => {
    if (user && user._id) {
      emitJoinUser();
    }
  }, [user]);

  useEffect(() => {
    if (isAuthInitialized) {
      dispatch(initializeCoordinates());
      const cleanupSocket = dispatch(initializeSocketListeners());
      loadGoogleMapsAPI();

      return () => cleanupSocket();
    }
  }, [dispatch, loadGoogleMapsAPI, isAuthInitialized]);

  useEffect(() => {
    const fetchCoordinates = async () => {
      if (isAuthenticated && !sessionCoordinates) {
        try {
          const { coordinates, source } = await getUserCoordinates();
          dispatch(setCoordinates({ coordinates, source }));
          const [lng, lat] = coordinates;
          const locationName = await reverseGeocode(lat, lng);
          dispatch(setLocationName(locationName));

        } catch (error) {
          console.error("Error fetching coordinates for logged-in user:", error);
        }
      }
    };

    fetchCoordinates();
  }, [isAuthenticated, sessionCoordinates, dispatch]);

  useEffect(() => {
    let retryCount = 0;
    const maxRetries = 3;
    const retryInterval = 8000;
  
    const interval = setInterval(async () => {
      if (!sessionCoordinates && retryCount < maxRetries) {
        try {
          const { coordinates, source } = await getUserCoordinates();
          dispatch(setCoordinates({ coordinates, source }));
          const [lng, lat] = coordinates;
          const locationName = await reverseGeocode(lat, lng);
          dispatch(setLocationName(locationName));
          clearInterval(interval);
        } catch (err) {
          retryCount++;
        }
      } else {
        clearInterval(interval);
      }
    }, retryInterval);
  
    return () => clearInterval(interval);
  }, [sessionCoordinates, dispatch]);

  //Display unread messages in footer
  useUnreadCounts();

  useEffect(() => {
    if (!isAuthInitialized) return;
  
    const token = AuthService.getToken();
    if (!token) return;
  
    try {
      const decodedToken = jwtDecode(token);
      const expirationTime = decodedToken.exp * 1000;
      const currentTime = Date.now();
  
      if (expirationTime < currentTime) {
        dispatch(logoutUser());
        setTimeout(() => setShowLogoutModal(true), 100);
      } else {
        const timeUntilExpiration = expirationTime - currentTime;
  
        const timeout = setTimeout(() => {
          dispatch(logoutUser());
          setShowLogoutModal(true);
        }, timeUntilExpiration);
  
        return () => clearTimeout(timeout);
      }
    } catch (error) {
      console.error("⚠️ Failed to decode token:", error);
      dispatch(logoutUser());
      setTimeout(() => setShowLogoutModal(true), 100);
    }
  }, [dispatch, isAuthInitialized, isAuthenticated]);
  
  const handleLogoutConfirm = () => {
    setShowLogoutModal(false);
    navigate("/");
  };


  if (!isAuthInitialized) {
    return <SplashScreen onComplete={() => setIsLoading(false)} />;
  }

  const hideHeaderFooterRoutes = ["/login", "/sign-up", "/reset-password/:token"];
  const shouldHideHeaderFooter = hideHeaderFooterRoutes.some((route) =>
    matchPath(route, location.pathname)
  );

  // Only show Header on home page
  const isHomePage = location.pathname === '/';
  const shouldShowHeader = isHomePage && !shouldHideHeaderFooter;

  // Social media links removed from global layout - now only in Profile page

  const locationBannerVisible = Boolean(tempLocationName) && !shouldShowHeader;
  const accountBannerRoutes = ["/login", "/sign-up", "/reset-password", "/reset-password/:token"];
  const isAccountFlowRoute = accountBannerRoutes.some((route) =>
    matchPath(route, location.pathname)
  );
  const showCreateAccountBanner = !locationBannerVisible && !isAccountFlowRoute;

  return (
    <>
      {isLoading ? (
        <SplashScreen onComplete={() => setIsLoading(false)} />
      ) : (
        <>
          {showLogoutModal && <LogoutModal onConfirm={handleLogoutConfirm} />}
          {/* Only show banner when Header is NOT visible (Header handles temp location on Home page) */}
          {tempLocationName && !shouldShowHeader && <TemporaryLocationBanner tempLocationName={tempLocationName} />}
          <CreateAccountBanner visible={showCreateAccountBanner} sticky={shouldShowHeader} />
          {shouldShowHeader && <Header />}
          <NewPostBanner />
          <ErrorRenderer />

          {/* Main Routes */}
          <AnimatePresence mode="wait">
            <Routes
              location={background || location}
              key={(background || location).pathname}
            >
              {routes.map(({ path, element: Element }) => (
                <Route
                  key={path}
                  path={path}
                  element={
                    <motion.div
                      initial="out"
                      animate="in"
                      exit="out"
                      variants={pageTransition}
                    >
                      <Element />
                    </motion.div>
                  }
                />
              ))}
            </Routes>
          </AnimatePresence>

          <AnimatePresence>
            {background && (
              <Routes>
                <Route
                  path="/notifications/:category/:imageId"
                  element={<FullScreenImageOverlay />}
                />
              </Routes>
            )}
          </AnimatePresence>
          <LegalFooter />
          {!shouldHideHeaderFooter && <Footer />}
          <CookieConsentBanner />
          <GatedAnalytics />
        </>
      )}
    </>
  );
}

export default App;
